
import { Vue, Component, Prop, Watch, Emit } from "vue-property-decorator";
import moment from "moment";
import { cloneDeep } from "lodash";
import LoadingOverlay from "./LoadingOverlay.vue";

import GET_PROJECTS from "@/apollo/scheduler/GetProjects.gql";

import Scheduler from "../SchedulerV2.vue";

import projectViewFiltersTemplate from "@/data/filtersTemplates/scheduler/projectViewFiltersTemplate";
import {
  Project,
  Project_Activity_Bool_Exp,
  Project_Bool_Exp,
  User,
} from "@/generated/graphql";
import SchedulerItemEntities from "@/entities/scheduler/SchedulerItemEntities";
import ProjectCriteriaOptions from "@/entities/scheduler/ProjectCriteriaOptions";
import ProjectVIewFiltersToWatch from "@/entities/scheduler/additionalFiltersInterfaces/ProjectViewFiltersToWatch";
import ProjectViewManager from "@/entities/scheduler/ProjectViewManager";
import ProjectViewActivity from "@/entities/scheduler/ProjectViewActivity";
import {
  getEmptyFiltersResultMessage,
  getEmptyListMessage,
} from "@/helpers/lists/emptyListMessages";
import ActivityDataForModal from "@/entities/modals/ActivityDataForModal";
import Roles from "@/entities/Roles";
import ProjectDataForModal from "@/entities/modals/ProjectDataForModal";
import ProjectAdvancedFilter from "../scheduler/filters/ProjectAdvancedFilter.vue";
import ProjectModalNew from "@/components/companyProjects/ProjectModalNew.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import ScheduleInfoIcon from "@/components/icons/ScheduleInfoIcon.vue";
import { getActivityPercentageComplete } from "@/utils/getActivityPercentage";
import { APP_COLORS } from "@/data/sidebar";
import { MessageDuration, MessageStatus } from "@/data/notificationsMessages";
import { NOTIFICATION } from "@/entities/Events";
import { EVENTS_BORDERS_COLORS, EVENTS_COLORS } from "@/data/schedularColors";
import adjustDates from "@/helpers/adjustTimeForScheduler";
// Import new components for top filters
import FormGroup from "@/components/form/FormGroupNew.vue";
import UiButtonMessage from "@/components/button/ui-button-message.vue";
import FormDatepicker from "@/components/form/FormDatepickerNew.vue";
import ProjectCriteriaMultiselect from "@/components/scheduler/schedulerSelects/ProjectCriteriaMultiselect.vue";
import ProjectStatusSelect from "@/components/scheduler/schedulerSelects/ProjectStatusSelect.vue";
import ProjectSchedulerEdit from "../ProjectSchedulerEdit.vue";

@Component({
  components: {
    Scheduler,
    ProjectAdvancedFilter,
    ActivityFormModal,
    ProjectModalNew,
    ScheduleInfoIcon,
    LoadingOverlay,
    // Add new components for top filters
    FormGroup,
    UiButtonMessage,
    FormDatepicker,
    ProjectCriteriaMultiselect,
    ProjectStatusSelect,
    ProjectSchedulerEdit
  },
   apollo: {
  raw_Projects: {
    query: GET_PROJECTS,
    variables() {
      return {
        where: this.filterConditionsForProject,
        activitiesWhere: this.activitiesWhere
      };
    },
    update: (data) => {
      return {
        projects: data.project,
        totalProjects: data.total_projects?.aggregate?.count || 0
      };
    },
  },
},
})
export default class ProjectScheduler extends Vue {
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Date, default: undefined }) readonly axisStart?: Date;
  @Prop({ type: Date, default: undefined }) readonly axisEnd?: Date;
  @Prop({ type: Array, default: () => [] })
  readonly selectedProjectsIds!: string[];
  @Prop({ type: Boolean, default: false })
  readonly showAdditionalFilters!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly shouldClearFilters!: boolean;
  @Prop({ type: Boolean, default: false }) showActivities!: boolean;

  // Add new filter properties
  searchTitle: string = "";
  searchLocation: string = "";
  searchDate: string = "";

  schedulerItemEntities = SchedulerItemEntities;
  queryStartTime: Date = moment().startOf("month").subtract(2, "w").toDate();
  queryEndTime: Date = moment().endOf("month").toDate();
  raw_Projects: { projects: Project[], totalProjects: number } = { totalProjects: 0, projects: [] };

  isInitialProjectsFetchingDone: boolean = false;
  numberOfAppliedFilters = 0;

  projects: Project[] = [];
  passedStartDate: Date = moment().startOf("month").toDate();
  passedEndDate: Date = moment().endOf("month").toDate();

  activityInfoForModal?: ActivityDataForModal | null = null;
  projectInfoForModal?: ProjectDataForModal | null = null;

  isSelectedItemManager: boolean = false;
  modalShown: boolean = false;
  modalType = "activity";
  newlyCreatedItemId?: string = undefined;
  contractorIdForModal?: string = "";
  isEdittingLocked: boolean = true;
  aggregateWhere: any = {}
  additionalFilters: ProjectVIewFiltersToWatch = cloneDeep(
    projectViewFiltersTemplate
  );

  created(): void {
    this.updateTotalCount(0)
    if (this.isManager) {
      this.aggregateWhere = {
        _and: [
          { company_id: { _eq: this.currentUser.id } },
          { manager: { user_id: { _eq: this.currentUser.id } } }
        ]
      }
    }
    this.$nextTick(() => {
    this.$apollo.queries.raw_Projects.setVariables({
      where: this.where,
      activitiesWhere: this.activitiesWhere
    });
    this.$apollo.queries.raw_Projects.skip = false;
    });

    this.$root.$on("projectsChanged", () => {
      this.$apollo.queries.raw_Projects.refetch();
    });
  }

  @Emit('update:updateCount')
  updateTotalCount(totalCount: number) {
    return totalCount;
  }

  @Watch("raw_Projects", { immediate: true })
  detectRawManagerProjects() {
    if (this.raw_Projects?.projects?.length) {
      this.projects = this.raw_Projects?.projects
    }
    this.updateTotalCount(this.raw_Projects?.totalProjects || 0)
  }
  message: string = "You haven't added any clients yet";

  get type() {
    return "Client";
  }

  get schedularType() {
    return `${this.type}s`;
  }

  // Add watcher for search filters
  get searchFiltersToWatch(): string {
    return JSON.stringify([
      this.searchTitle,
      this.searchLocation,
      this.searchDate
    ]);
  }

  @Watch("searchFiltersToWatch")
  handleSearchFiltersChange() {
    this.projects = [];
    this.$apollo.queries.raw_Projects.refetch({
      where: this.where,
      activitiesWhere: this.activitiesWhere,
    });
  }

  // Handle filter dropdowns change
  handleFilterChange() {
    this.handleSearchFiltersChange();
  }

  // Handle reset button click
  handleReset() {
    this.searchTitle = "";
    this.searchLocation = "";
    this.searchDate = "";
    this.additionalFilters = cloneDeep(projectViewFiltersTemplate);
    this.$emit("filtersReset");
    this.handleSearchFiltersChange();
  }

  @Watch("propToWatch")
  refetchOnDataFiltersChange(oldVal: string[], newVal: string[]) {
    if (oldVal !== newVal) {
      this.projects = []
      this.$apollo.queries.raw_Projects.refetch({
        where: this.where,
        activitiesWhere: this.activitiesWhere,
      });
    }
  }

  @Watch("numberOfAppliedFilters", { immediate: true })
  appliedFiltersChanged() {
    this.$emit("appliedFiltersChanged", this.numberOfAppliedFilters);
  }

  get loading(): boolean {
    return this.$apollo.queries.raw_Projects.loading && !this.projects.length;
  }

  get shouldBlockEditting(): boolean {
    return this.isEdittingLocked && !Boolean(this.activityInfoForModal?.isNew);
  }

  get resources() {
    const data = this.projects;
    const processData = data.map((project, i) => {
      const activityMaxEndDate = project.client_care_plan?.care_plan_start_date;
      const activityMinStartDate = project.client_care_plan?.care_plan_end_date;
      const children = [];
      const color = EVENTS_COLORS[i % EVENTS_COLORS.length];
      let totalTaks = 0;

      project.project_activities.forEach((activity, index) => {
        totalTaks += 1

        const percentageComplete = getActivityPercentageComplete(activity);
        const startDate = activity.start_date ? new Date(activity.start_date) : null;
        const endDate = activity.end_date ? new Date(activity.end_date) : null;

        // if (activityMaxEndDate === null) {
        //   activityMaxEndDate = new Date(activity.end_date)
        //   activityMinStartDate = new Date(activity.start_date)
        // }
        // else if (startDate && endDate) {
        //   activityMaxEndDate = activityMaxEndDate.getTime() - endDate.getTime() > 0 ? activityMaxEndDate : endDate;
        //   activityMinStartDate = activityMinStartDate.getTime() - startDate.getTime() > 0 ? startDate : activityMinStartDate;
        // }
        children.push({
          id: activity.id,
          index: index + 1,
          title: moment(activity.start_date).format("DD/MM/YYYY"),
          name: moment(activity.start_date).format("DD/MM/YYYY"),
          type: "activity",
          start_date: activity.start_date,
          end_date: activity.end_time,
          completed: activity.completed,
          location: activity.location,
          virtual: activity.virtual || false,
          virtual_meeting_link: activity.virtual_meeting_link || null,
          fallback_num: activity.fallback_num || null,
          note: activity.note,
          budget: activity.budget,
          contractor: activity.contractor,
          confirmed_hours: activity.confirmed_hours,
          progress: percentageComplete,
          missingResource: !activity.contractor_id,

          color: color,
          entityInfo: {
            project,
            activity,
          },
        });
      });
      const projectColor = APP_COLORS[EVENTS_BORDERS_COLORS[i % EVENTS_BORDERS_COLORS.length]];
      return {
        ...project,
        type: "project",
        level: 1,
        color: color,
        title: project.client_name,
        subtitle: project.company?.business_name,
        name: project.client_name,
        noOftasks: project.project_activities.length || 0,
        missingResource: !project.project_activities.length,
        missingManager: !project.manager_id,
        borderColor: projectColor,
        noOfTotalTaks: totalTaks,
        children,
        entityInfo: {
          project,
          activityMaxEndDate,
          activityMinStartDate
        },
      };
    });

    return processData;
  }

  get events() {
    const events = this.projectActivityItems;
    const me = events.map((e) => {
      return {
        ...e,
        resourceId: e.id,
        startDate: new Date(e.start),
        endDate: new Date(e.end),
        name: e.content,
      };
    });
    return me;
  }

  get listeners() {
    return {
      cellClick: ({ record, event }) => {
        const expanderClass = 'b-tree-expander'
        const isClassNameString = typeof event.target.className === 'string'
        const isExpander = isClassNameString && event.target?.className?.includes(expanderClass) //The className property on an SVG element has a different definition from the className property of an HTML element.In HTML, className is a string. In SVG it is an SVGAnimatedString object
        // Add a new client when clicking plus icon
        const eventdata = record.eventStore.getById(record.data?.id)
        if (isExpander) return false;
        if (event.target.closest(".b-add-activity")) {
          this.handleCreateBtnClicked(record.data);
        } else if (record.data.type === 'activity') {
          this.handleActivityEventClicked(eventdata.data?.entityInfo);
        }
        else if (record.data.type === 'project') {
          this.handleProjectEventClicked(eventdata.data?.entityInfo);
        }
        return false;
      },
      eventClick: (props) => {
        const { eventRecord } = props;
        //eventRecord.data
        if (eventRecord.data.type === "project") {
          this.handleProjectEventClicked(eventRecord.data.entityInfo);
        }
        if (eventRecord.data.type === "activity") {
          this.handleActivityEventClicked(eventRecord.data.entityInfo);
        }
      },
      beforeEventAdd: () => {
        return false;
      },
      beforeEventResize: ({ eventRecord }) => {
        if (eventRecord.data.type === "contractor") {
          return false;
        }
        return true;
      },

      beforeEventResizeFinalize: ({ source, context }) => {
        context.async = true;

        const { startDate: newStartDate, endDate: newEndDate, eventRecord } = context;
        const { entityInfo: { project, activityMaxEndDate,
          activityMinStartDate }, type } = eventRecord.data;
        const { id: projectId } = project
        const sdate = new Date(newStartDate.toISOString()).getTime();
        const edate = new Date(newEndDate.toISOString()).getTime();

        let errorMsg = null;
        if (type === "activity") {

          const parentSource = source.eventStore.getById(projectId);
          const { startDate, endDate, } = parentSource.data;
          const psdate = new Date(new Date(startDate).toISOString()).getTime()
          const pedate = new Date(new Date(endDate).toISOString()).getTime()

          // const isTimeGreaterOrLessthanProject = edate > pedate || sdate < psdate

          // if (isTimeGreaterOrLessthanProject) {
          //   errorMsg = "Activity Start and End Dates must be between Project Start and End Dates."
          // }

        }
        if (type === "project") {
          // if (activityMaxEndDate && activityMinStartDate) {

          //   const maxActivityEndTime = moment(activityMaxEndDate).valueOf();
          //   const minActivityStartTime = moment(activityMinStartDate).valueOf();
          //   const isTimeGreaterOrLessthanActivity = edate < maxActivityEndTime || sdate > minActivityStartTime
          //   if (isTimeGreaterOrLessthanActivity) {
          //     errorMsg = "Project Start and End Dates must be greater than activities Start and End Dates."
          //   }
          // }
        }
        if (errorMsg) {
          this.$eventBus.emit(NOTIFICATION, {
            status: MessageStatus.ERROR,
            title: errorMsg,
            duration: MessageDuration.DEFAULT,
            // message: `You rejected hours for ${this.selectedRows.length || 0} resources.`,
          });
          return context.finalize(false);
        }
        context.finalize(true);

      },
      eventResizeEnd: async ({ source, eventRecord, changed, event }) => {
        if (eventRecord.data.type === "contractor" || !changed) {
          return false;
        }
        const { id, resourceId, startDate, endDate, entityInfo: { project } } = eventRecord.data;
        const start_date = moment(startDate);
        const end_date = moment(endDate);
        const newSMilis = start_date.valueOf();
        const newEMilis = end_date.valueOf();
        const { id: projectId } = project
        const parentEventSource = source.eventStore.getById(projectId);
        const activitysource = source.eventStore.getById(resourceId);
        const parentReSource = source.resourceStore.getById(projectId);
        const activityResource = source.resourceStore.getById(resourceId);

        if (eventRecord.data.type === "activity") {
          const entityInfo = { ...parentEventSource.entityInfo }
          entityInfo.activityMaxEndDate = new Date(new Date(entityInfo.activityMaxEndDate).toISOString()).getTime() < newEMilis ? endDate : entityInfo.activityMaxEndDate
          entityInfo.activityMinStartDate = new Date(new Date(entityInfo.activityMinStartDate).toISOString()).getTime() > newSMilis ? startDate : entityInfo.activityMinStartDate
          parentEventSource.entityInfo = entityInfo
          parentReSource.entityInfo = entityInfo
          activitysource.start = startDate
          activitysource.end = endDate
          activityResource.start_date = startDate
          activityResource.end_date = endDate
          this.$emit("updateBasicActivityData", id, { start_date, end_date });

          return true;
        }
        if (eventRecord.data.type === "project") {
          activityResource.start_date = startDate
          activityResource.end_date = endDate
          activitysource.start = startDate
          activitysource.end = endDate
          this.$emit("updateProject", {
            set: {
              start_date,
              end_date,
            },
            projectId: eventRecord.data.entityInfo.project.id,
            slug: eventRecord.data.entityInfo.project.slug,
            addSkills: [],
            deleteSkillsIds: [],
            customSkills: [],
            updatePersonalSkills: [],
          });
          return true;
        }
        return false;
      },
    };
  }

  get customPlaceholderMessage(): string {
    if (this.numberOfAppliedFilters && !this.projects.length) {
      return getEmptyFiltersResultMessage("projects");
    }
    return getEmptyListMessage("projects");
  }

  @Watch("projectIds")
  updateSelectedIds(): void {
    if (this.isInitialProjectsFetchingDone) {
      return;
    }
    this.$emit("selectedProjectIdsUpdated", this.projectIds);
    this.isInitialProjectsFetchingDone = true;
  }

  get propToWatch(): string {
    return JSON.stringify([this.selectedProjectsIds, this.additionalFilters]);
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get projectIds(): string[] {
    return this.projects.map((project) => project.id);
  }

  get projectActivityItems(): (ProjectViewManager | ProjectViewActivity)[] {
    if (!this.projects) {
      return [];
    }
    const items = [];
    this.projects.forEach((project) => {
      let activityMaxEndDate: Date = null;
      let activityMinStartDate: Date = null;
      const activitiesArray = project.project_activities.map((activity) => {
        const startDate = activity.start_date ? new Date(activity.start_date) : null;
        const endDate = activity.end_date ? new Date(activity.end_date) : null;

        if (activityMaxEndDate === null) {
          activityMaxEndDate = new Date(activity.end_date)
          activityMinStartDate = new Date(activity.start_date)
        }
        else if (startDate && endDate) {
          activityMaxEndDate = activityMaxEndDate.getTime() - endDate.getTime() > 0 ? activityMaxEndDate : endDate;
          activityMinStartDate = activityMinStartDate.getTime() - startDate.getTime() > 0 ? startDate : activityMinStartDate;
        }
        const [adjustedEnd, adjustedStart] = adjustDates(activity.end_date, activity.start_date, project.start_date);
        return {
          id: activity.id,
          start: activity.start_time,
          end: activity.end_time,
          content: moment(activity.start_date).format("DD/MM/YYYY"),
          type: "activity",
          entityInfo: {
            activity,
            project,
            // projectStartDate: project.start_date,
            // projectEndDate: project.end_date,
          },
        };
      })
      const projectObj = {
        id: project.id,
        start: project.start_date,
        end: project.end_date,
        start_date: project.start_date,
        end_date: project.end_date,
        content: project.name,
        type: "project",
        entityInfo: {
          project,
          activityMaxEndDate,
          activityMinStartDate
        },
      };
      items.push(
        projectObj,
        ...activitiesArray
      );
    });
    return items;
  }

  get activitiesIds(): string[] {
    return this.projects
      .map((project) => {
        return project.project_activities.map((activity) => activity.id);
      })
      .flat();
  }

  get activitiesWhere(): Project_Activity_Bool_Exp {
    return { drafted: { _neq: true } }
  }

  get where(): Project_Bool_Exp {
    const filterConditions = [];
    const { countries, states, cities, status, criteria, projects } =
      this.additionalFilters;

    const statusIds = status.map((status) => status.id);
    const criteriaNames = criteria.map((criteria) => criteria.name);

    if (this.selectedProjectsIds.length || projects.length) {
      const ids = [...this.selectedProjectsIds, ...projects]
      filterConditions.push({
        id: {
          _in: ids,
        },
      });
    }

    // Add title/name filter
    if (this.searchTitle && this.searchTitle.trim()) {
      filterConditions.push({
        _or: [
          { client_name: { _ilike: `%${this.searchTitle}%` } },
          {
            company: {
              business_name: { _ilike: `%${this.searchTitle}%` }
            }
          }
        ]
      });
    }

    // Add location/address filter
    if (this.searchLocation && this.searchLocation.trim()) {
      filterConditions.push({
        _or: [
          { address: { _ilike: `%${this.searchLocation}%` } },
          { location: { _ilike: `%${this.searchLocation}%` } },
          { city: { _ilike: `%${this.searchLocation}%` } },
          { state: { _ilike: `%${this.searchLocation}%` } },
          { country: { _ilike: `%${this.searchLocation}%` } }
        ]
      });
    }

    // Add date filter
    if (this.searchDate) {
      const formattedDate = moment(this.searchDate).format('YYYY-MM-DD');
      filterConditions.push({
        _or: [
          // Project start date matches
          { start_date: { _gte: formattedDate, _lt: moment(formattedDate).add(1, 'day').format('YYYY-MM-DD') } },
          // Project end date matches
          { end_date: { _gte: formattedDate, _lt: moment(formattedDate).add(1, 'day').format('YYYY-MM-DD') } },
          // Date is between project start and end
          {
            _and: [
              { start_date: { _lte: formattedDate } },
              { end_date: { _gte: formattedDate } }
            ]
          }
        ]
      });
    }

    if (countries.length) {
      filterConditions.push({
        country: { _in: countries },
      });
    }

    if (states.length) {
      filterConditions.push({
        state: { _in: states },
      });
    }

    if (cities.length) {
      filterConditions.push({
        city: { _in: cities },
      });
    }

    if (criteriaNames.length) {
      if (
        criteriaNames.includes(
          ProjectCriteriaOptions.PROJECTS_NEEDS_CONTRACTORS
        )
      ) {
        filterConditions.push({
          project_activities: {
            contractor_id: {
              _is_null: true,
            },
          },
        });
      }

      if (
        criteriaNames.includes(ProjectCriteriaOptions.PROJECTS_WITHOUT_MANAGER)
      ) {
        filterConditions.push({
          manager_id: {
            _is_null: true,
          },
        });
      } else {
        if (this.isManager) {

          filterConditions.push({
            manager: {
              user_id: {
                _eq: this.currentUser.id
              }
            },
          });
        }

      }

      if (
        criteriaNames.includes(
          ProjectCriteriaOptions.PROJECTS_WITHOUT_ACTIVITIES
        )
      ) {
        filterConditions.push({
          _not: { project_activities: {} },
        });
      } else {
        // filterConditions.push({
        //   project_activities: {}
        // });

      }
    } else {
      // filterConditions.push({
      //   project_activities: {}
      // });
      if (this.isManager) {

        filterConditions.push({
          manager: {
            user_id: {
              _eq: this.currentUser.id
            }
          },
        });
      }
    }

    if (statusIds.length && statusIds[0] !== "All") {
      filterConditions.push({
        _and: [{ completed: { _eq: statusIds[0] !== "Active" } }],
      });
    }
    return { _and: [...filterConditions, { company_id: { _eq: this.companyId } }] };
  }

  handleIconClose(): void {
    this.$emit("close");
    this.$apollo.queries.raw_Projects.refetch();
  }

  handleCreateBtnClicked(activity) {
    this.activityInfoForModal = null;
    this.projectInfoForModal = activity.entityInfo.project;
    this.contractorIdForModal = activity.group;
    this.modalType = "activity";
    this.modalShown = true;
  }

  handleAddActivityClicked(activity: any) {
    this.activityInfoForModal = {
      title: "",
      contractor_id: activity.group,
      end_date: "",
      note: "",
      budget: "",
      confirmed_hours: 0,
      requested_hours: 0,
      isNew: true,
    };
    // this.setCurrentlyFetchedItems();
    const projectId = (activity as any).projectId;
    this.projectInfoForModal = {
      id: projectId,
      start_date: activity.project.start_date,
      end_date: activity.project.end_date,
    };
    this.contractorIdForModal = activity.group;
    this.modalShown = true;
    this.modalType = "activity";
  }

  handleActivityEventClicked({ activity, project }: any) {
    this.activityInfoForModal = activity;
    this.projectInfoForModal = project;
    this.modalShown = true;
    this.modalType = "activity";
  }

  handleProjectEventClicked({ activity, project }: any) {
    this.activityInfoForModal = activity;
    this.projectInfoForModal = project;
    this.modalShown = true;
    this.modalType = "project";
  }

  handleCloseModal(): void {
    if (this.modalShown == false) {
      return;
    }

    if (this.newlyCreatedItemId) {
      this.clearNewlyCreatedItemId();
    }
    this.closeModal();
  }

  async handleCollectionUpdated(): Promise<void> {
    this.clearNewlyCreatedItemId();
    await this.refetchCollection();
    this.closeModal();
  }

  clearNewlyCreatedItemId(): void {
    this.newlyCreatedItemId = undefined;
  }

  closeModal(): void {
    this.modalShown = false;
    this.isEdittingLocked = true;
  }

  async refetchCollection(): Promise<void> {
    this.$apollo.queries.raw_Projects.refetch({
      where: this.where,
      activitiesWhere: this.activitiesWhere,
    });
  }

  handleAppliedFiltersNumberChanged(num: number): void {
    this.numberOfAppliedFilters = num;
  }

  handleApplyFilters(
    filters: ProjectVIewFiltersToWatch & {
      rangeStart: Date;
      rangeEnd: Date;
      showActivities: boolean;
    }
  ): void {
    this.additionalFilters.countries = filters.countries;
    this.additionalFilters.states = filters.states;
    this.additionalFilters.cities = filters.cities;
    this.additionalFilters.criteria = filters.criteria;
    this.additionalFilters.status = filters.status;
    this.additionalFilters.projects = filters.projects;
    this.passedEndDate = filters.rangeEnd;
    this.passedStartDate = filters.rangeStart;
    this.showActivities = filters.showActivities;
  }
}
